/**
 * Товары: `GET /admin/products`, `GET/PATCH /admin/products/{id}`, остатки,
 * продажи, фото и массовая смена категории.
 *
 * Номенклатуру, цену и остатки ведёт FOM — админка их только показывает (R-15).
 * Менять можно категорию, кэшбэк, рецептурность, витрину и тексты.
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { mediaUrl, type ApiClient, type components } from '@dorixona/api'
import { parseMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import type { ProductState } from './model'

type ProductDto = components['schemas']['Product']
type StockDto = components['schemas']['Stock']

export interface ProductTexts {
  desc: string
  indications: string
  contra: string
  dosage: string
  storage: string
}

export interface Product {
  id: number
  name: string
  /** Код в учётной системе: по нему товар сверяют с FOM. */
  fomCode: string
  maker: string
  categoryId: number
  categoryName: string
  /** Свой процент товара; null — берём процент категории (R-12). */
  cbOwn: number | null
  /** Итоговый процент, уже с учётом категории. */
  cb: number
  rx: boolean
  popular: boolean
  active: boolean
  /** Готовые адреса картинок: их нельзя собирать самим (R-…, см. «Картинки»). */
  photos: string[]
  /** Те же фото с номерами: удаляют фото по номеру, а не по адресу. */
  photoItems: ProductPhoto[]
  /** Наименьшая активная цена по сети, сум; 0 — цены нет (F-44). */
  price: number
  inStock: boolean
  /** Остаток по всей сети: склад FOM минус то, что держат живые заказы (R-26). */
  stockQty: number
  /** Когда FOM последний раз прислал остатки по товару, мс; 0 — ещё не присылал. */
  syncedAt: number
  ru: ProductTexts
  uz: ProductTexts
}

export interface ProductPhoto {
  id: number
  url: string
}

export interface Stock {
  storeId: number
  storeName: string
  district: string
  qty: number
  price: number
}

const EMPTY_TEXTS: ProductTexts = {
  desc: '',
  indications: '',
  contra: '',
  dosage: '',
  storage: '',
}

function toTexts(
  dto: components['schemas']['ProductTexts'] | undefined,
  lang: string,
): ProductTexts {
  const one = dto?.[lang]
  if (!one) return EMPTY_TEXTS
  return {
    desc: one.description ?? '',
    indications: one.indications ?? '',
    contra: one.contraindications ?? '',
    dosage: one.dosage ?? '',
    storage: one.storage ?? '',
  }
}

export function toProduct(dto: ProductDto): Product {
  return {
    id: dto.id ?? 0,
    name: dto.name_ru ?? '',
    fomCode: dto.fom_code ?? '',
    maker: dto.maker ?? '',
    categoryId: dto.category_id ?? 0,
    categoryName: dto.category ?? '',
    cbOwn: dto.cashback_pct_override ?? null,
    cb: dto.cashback_pct ?? 0,
    rx: dto.is_rx ?? false,
    popular: dto.is_popular ?? false,
    active: dto.is_active ?? true,
    photos: (dto.photos ?? []).map((path) => mediaUrl(path, env.apiBaseUrl)),
    photoItems: (dto.photo_items ?? []).map((p) => ({
      id: p.id ?? 0,
      url: mediaUrl(p.url ?? '', env.apiBaseUrl),
    })),
    price: dto.price ?? 0,
    inStock: dto.in_stock ?? false,
    stockQty: dto.stock_qty ?? 0,
    syncedAt: parseMs(dto.fom_synced_at),
    ru: toTexts(dto.texts, 'ru'),
    uz: toTexts(dto.texts, 'uz'),
  }
}

function fromTexts(texts: ProductTexts) {
  return {
    description: texts.desc,
    indications: texts.indications,
    contraindications: texts.contra,
    dosage: texts.dosage,
    storage: texts.storage,
  }
}

export interface ProductQuery {
  q?: string
  categoryId?: number
  /** Товары, которым FOM ещё не назначил категорию. */
  uncategorized?: boolean
  noPhoto?: boolean
  /** Порог «заканчивается»: остаток по сети меньше этого числа. */
  lowStock?: number
  rx?: 'rx' | 'otc' | ''
  inStock?: boolean
  active?: boolean
  /** Порядок выдачи, который понимает сервер. */
  sort?: 'popular' | 'price_asc' | 'price_desc' | 'name' | ''
  page?: number
  pageSize?: number
}

export interface ProductPage {
  items: Product[]
  total: number
  page: number
  pageSize: number
}

function toQuery(query: ProductQuery): Record<string, string | number | boolean | undefined> {
  return {
    q: query.q || undefined,
    category_id: query.categoryId || undefined,
    uncategorized: query.uncategorized || undefined,
    no_photo: query.noPhoto || undefined,
    low_stock: query.lowStock || undefined,
    rx: query.rx || undefined,
    in_stock: query.inStock ?? undefined,
    is_active: query.active ?? undefined,
    sort: query.sort || undefined,
    page: query.page ?? 1,
    page_size: query.pageSize ?? 10,
  }
}

export async function fetchProducts(api: ApiClient, query: ProductQuery): Promise<ProductPage> {
  const page = await api.get<{
    items?: ProductDto[]
    total?: number
    page?: number
    page_size?: number
  }>('/admin/products', { query: toQuery(query) })
  return {
    items: (page.items ?? []).map(toProduct),
    total: page.total ?? 0,
    page: page.page ?? 1,
    pageSize: page.page_size ?? 10,
  }
}

/** Поля, которые ведёт админка. Отправляем только то, что действительно меняем. */
export interface ProductPatch {
  categoryId?: number | null
  cbOwn?: number | null
  rx?: boolean
  popular?: boolean
  active?: boolean
  ru?: ProductTexts
  uz?: ProductTexts
}

export function toPatchBody(patch: ProductPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (patch.categoryId !== undefined) body.category_id = patch.categoryId || null
  if (patch.cbOwn !== undefined) body.cashback_pct_override = patch.cbOwn
  if (patch.rx !== undefined) body.is_rx = patch.rx
  if (patch.popular !== undefined) body.is_popular = patch.popular
  if (patch.active !== undefined) body.is_active = patch.active
  if (patch.ru || patch.uz) {
    const texts: Record<string, unknown> = {}
    if (patch.ru) texts.ru = fromTexts(patch.ru)
    if (patch.uz) texts.uz = fromTexts(patch.uz)
    body.texts = texts
  }
  return body
}

export function useProducts(query: ProductQuery, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.products(toQuery(query)),
    queryFn: () => fetchProducts(api, query),
    enabled,
    // Пока грузится следующая страница, прежняя остаётся на экране: таблица
    // не мигает скелетом на каждом листании.
    placeholderData: keepPreviousData,
  })
}

/**
 * Числа у пунктов фильтров таблицы (`GET /admin/products/counts`): по каждому
 * состоянию, «без категории» и каждой категории — по всему каталогу, скрытые
 * товары тоже. Сервер считает так же, как фильтрует, поэтому число и строки
 * под пунктом всегда сходятся.
 */
export interface ProductCounts {
  states: Partial<Record<ProductState, number>>
  uncategorized: number
  categories: Record<number, number>
}

export async function fetchProductCounts(api: ApiClient): Promise<ProductCounts> {
  const dto = await api.get<{
    states?: Record<string, number>
    uncategorized?: number
    categories?: Record<string, number>
  }>('/admin/products/counts')
  const categories: Record<number, number> = {}
  for (const [id, count] of Object.entries(dto.categories ?? {})) categories[Number(id)] = count
  return { states: dto.states ?? {}, uncategorized: dto.uncategorized ?? 0, categories }
}

export function useProductCounts() {
  const api = useApi()
  return useQuery({ queryKey: keys.productCounts, queryFn: () => fetchProductCounts(api) })
}

export function useProduct(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.product(id),
    queryFn: async () => toProduct(await api.get<ProductDto>(`/admin/products/${id}`)),
    enabled: id > 0,
  })
}

export function useProductStock(id: number, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.productStock(id),
    queryFn: async () => {
      const page = await api.get<{ items?: StockDto[] }>(`/admin/products/${id}/stock`)
      return (page.items ?? []).map<Stock>((s) => ({
        storeId: s.store_id ?? 0,
        storeName: s.store ?? '',
        district: s.district ?? '',
        qty: s.qty ?? 0,
        price: s.price ?? 0,
      }))
    },
    enabled,
  })
}

export function useSaveProduct() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: ProductPatch }) =>
      toProduct(await api.patch<ProductDto>(`/admin/products/${id}`, toPatchBody(patch))),
    onSuccess: (product) => {
      client.setQueryData(keys.product(product.id), product)
      return client.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

/**
 * Фотография товара. Файл уходит полем формы `file`; тип сервер определяет по
 * байтам, а не по имени. Ответ — товар целиком с новым адресом картинки:
 * адрес нельзя собирать самим, файлы переедут в объектное хранилище.
 */
export function useUploadPhoto() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const form = new FormData()
      form.append('file', file)
      return toProduct(await api.post<ProductDto>(`/admin/products/${id}/photo`, form))
    },
    onSuccess: (product) => {
      client.setQueryData(keys.product(product.id), product)
      return client.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

/**
 * Удаление фотографии по её номеру из `photo_items`. Ответ — просто «готово»,
 * поэтому товар перечитываем: `['products']` накрывает и карточку
 * (`keys.product`), и список (`keys.products`), где тоже видно главное фото и
 * признак «без фото».
 */
export function useDeletePhoto() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, photoId }: { id: number; photoId: number }) =>
      api.del<unknown>(`/admin/products/${id}/photos/${photoId}`),
    // Один ключ-префикс вместо двух вызовов: второй вызов отменил бы уже
    // начатое перечитывание карточки и запустил его заново.
    onSuccess: () => client.invalidateQueries({ queryKey: ['products'] }),
  })
}

/** Продажи товара за период: по дню на точку плюс итоги. */
export interface Sales {
  byDay: { label: string; qty: number; value: number }[]
  qty: number
  revenue: number
}

export function useProductSales(id: number, from: string, to: string, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.productSales(id, from, to),
    queryFn: async () => {
      const dto = await api.get<{
        by_day?: components['schemas']['Point'][]
        qty?: number
        revenue?: number
      }>(`/admin/products/${id}/sales`, { query: { from, to } })
      return {
        byDay: (dto.by_day ?? []).map((p) => ({
          label: p.label ?? '',
          qty: p.count ?? 0,
          value: p.value ?? 0,
        })),
        qty: dto.qty ?? 0,
        revenue: dto.revenue ?? 0,
      } satisfies Sales
    },
    enabled,
  })
}

/** Массовая раскладка товаров по категориям: до 500 идентификаторов за раз. */
export function useBulkCategory() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: number[]; categoryId: number | null }) => {
      const done = await api.post<{ updated?: number }>('/admin/products/bulk-category', {
        product_ids: ids,
        category_id: categoryId,
      })
      return done.updated ?? 0
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['products'] })
      await client.invalidateQueries({ queryKey: keys.categories })
    },
  })
}
